import { Progress } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { keepPreviousData, skipToken, useQuery } from '@tanstack/react-query';
import { LngLatBounds, MapEvent } from 'mapbox-gl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMap } from 'react-map-gl';

import { ResultsMapColorMode } from '../data-access/types/results-map';
import { useResultsQueries } from '../data-access/queries/results';
import {
  applyResultsMapColors,
  parseResultsMapGeoJson,
  toClusterLayerData,
} from '../utils/results-map-legend';

import { ResultsMapLegendPanel } from './results-map-legend-panel';

import { PlotsLayer, ClusterLayer, zoomOnFeature } from '@map';
import { useFiltersStore } from '@data-access';

export const RESULTS_LAYER_ID = 'results-layer';
export const RESULTS_CLUSTER_LAYER_ID = 'results-cluster-layer';

export const RESULTS_INTERACTIVE_LAYER_IDS = [
  RESULTS_LAYER_ID,
  RESULTS_CLUSTER_LAYER_ID,
  `${RESULTS_CLUSTER_LAYER_ID}_points`,
];

/**
 * Fetches results/map by current viewport bounds (same contract as plotmap)
 * and renders PlotsLayer + ClusterLayer. Must be used inside PfMap.
 */
export function ResultsMapInBounds() {
  const { current: map } = useMap();
  const [mapBounds, setMapBounds] = useState<LngLatBounds | null>(null);
  const [mapZoom, setMapZoom] = useState(0);
  const [colorMode, setColorMode] = useState<ResultsMapColorMode>('ges_status');
  const hasZoomedRef = useRef(false);

  const cluster = mapZoom < 12;
  const [debouncedBounds] = useDebouncedValue(mapBounds, 400);
  const queries = useResultsQueries();
  const columnFilters = useFiltersStore.use['results']().filters;

  const mapQueryParams = useMemo(
    () =>
      debouncedBounds
        ? {
            bounds: debouncedBounds,
            cluster,
            zoom: mapZoom,
            tableFilters: {
              page: 1,
              sorting: [],
              filters: columnFilters,
            },
          }
        : null,
    [cluster, columnFilters, debouncedBounds, mapZoom],
  );

  const query = useQuery(
    mapQueryParams
      ? {
          ...queries.mapList(mapQueryParams),
          placeholderData: keepPreviousData,
        }
      : {
          queryKey: ['results', 'map', 'pending-bounds'],
          queryFn: skipToken,
        },
  );

  const handleMapIdle = useCallback((e: MapEvent) => {
    setMapBounds(e.target.getBounds());
    setMapZoom(e.target.getZoom());
  }, []);

  useEffect(() => {
    if (map) {
      map.on('idle', handleMapIdle);
      return () => {
        map.off('idle', handleMapIdle);
      };
    }
  }, [map, handleMapIdle]);

  const parsedMap = useMemo(() => {
    if (!query.data) return null;
    return parseResultsMapGeoJson(query.data);
  }, [query.data]);

  const activeLegend = useMemo(() => {
    if (!parsedMap) return [];
    return colorMode === 'carbon'
      ? parsedMap.carbonLegend
      : parsedMap.gesStatusLegend;
  }, [colorMode, parsedMap]);

  const coloredData = useMemo(() => {
    if (!parsedMap) return null;
    return applyResultsMapColors(parsedMap.featureCollection, colorMode);
  }, [colorMode, parsedMap]);

  useEffect(() => {
    const mapInstance = map?.getMap();
    const featureCollection = parsedMap?.featureCollection;
    const features = featureCollection?.features;
    if (
      features?.length &&
      featureCollection &&
      mapInstance &&
      !hasZoomedRef.current
    ) {
      zoomOnFeature({ map: mapInstance, geojson: featureCollection });
      hasZoomedRef.current = true;
    }
  }, [map, parsedMap]);

  return (
    <>
      <ResultsMapLegendPanel
        colorMode={colorMode}
        legend={activeLegend}
        onColorModeChange={setColorMode}
      />

      {query.isRefetching ? (
        <Progress
          animated
          opacity={0.9}
          radius={0}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            right: 0,
            zIndex: 1,
          }}
          value={100}
        />
      ) : null}

      {coloredData && (
        <>
          <PlotsLayer
            data={coloredData}
            id={RESULTS_LAYER_ID}
            minZoom={12}
          />
          <ClusterLayer
            clusterColor="#ED7D31"
            data={toClusterLayerData(coloredData)}
            layerId={RESULTS_CLUSTER_LAYER_ID}
            pointsMaxZoom={12}
          />
        </>
      )}
    </>
  );
}
